/**
 * WizardSteps Component
 * Visual progress indicator for the wizard workflow
 * Uses custom icons matching the Flutter app design
 */

import React from 'react';
import {Box, Step, StepButton, StepLabel, Stepper, StepIconProps} from '@mui/material';
import {
  Description as RequirementsIcon,
  AccountTree as PlanIcon,
  PlayCircleOutline as GenerateIcon,
  VerifiedOutlined as ValidateIcon,
  Check as CheckIcon,
} from '@mui/icons-material';

interface WizardStepsProps {
  activeStep: number; // 0-3
  steps: string[]; // e.g., ['Requirements', 'Plan', 'Generate', 'Validate']
  onStepClick?: (step: number) => void; // Optional click handler
  canNavigateToStep?: (step: number) => boolean; // Check if step is accessible
  isStepCompleted?: (step: number) => boolean; // Check if step has data/is completed
}

// Map step index to icon
const STEP_ICONS: Record<number, React.ReactElement> = {
  1: <RequirementsIcon sx={{fontSize: 20}} />,
  2: <PlanIcon sx={{fontSize: 20}} />,
  3: <GenerateIcon sx={{fontSize: 20}} />,
  4: <ValidateIcon sx={{fontSize: 20}} />,
};

// Custom step icon component matching Flutter's design
const CustomStepIcon: React.FC<StepIconProps> = ({active, completed, icon}) => {
  const bgColor = completed
    ? 'success.main'
    : active
      ? 'primary.main'
      : 'grey.300';

  const color = completed || active ? 'common.white' : 'grey.500';

  return (
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: '50%',
        bgcolor: bgColor,
        color: color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s ease',
        boxShadow: active ? '0 2px 8px rgba(0,119,139,0.3)' : 'none',
      }}
    >
      {completed ? (
        <CheckIcon sx={{fontSize: 20}} />
      ) : (
        STEP_ICONS[icon as number] || icon
      )}
    </Box>
  );
};

export const WizardSteps: React.FC<WizardStepsProps> = ({
  activeStep,
  steps,
  onStepClick,
  canNavigateToStep,
  isStepCompleted,
}) => {
  const handleStepClick = (index: number) => {
    // Check if navigation is allowed
    if (canNavigateToStep && !canNavigateToStep(index)) {
      return; // Ignore click if not allowed
    }
    // Call parent handler
    if (onStepClick) {
      onStepClick(index);
    }
  };

  return (
    <Box sx={{width: '100%'}}>
      <Stepper activeStep={activeStep} alternativeLabel>
        {steps.map((label, index) => {
          // A step is completed if it has data (determined by parent)
          const completed = isStepCompleted ? isStepCompleted(index) : activeStep > index;
          const canNavigate = canNavigateToStep ? canNavigateToStep(index) : true;
          const isActive = activeStep === index;

          return (
            <Step key={label} completed={completed}>
              <StepButton
                onClick={() => handleStepClick(index)}
                disabled={!canNavigate}
                sx={{
                  cursor: canNavigate ? 'pointer' : 'default',
                  '& .MuiStepLabel-root': {
                    ...(isActive && {
                      '& .MuiStepLabel-label': {
                        fontWeight: 600,
                        color: 'primary.main',
                      },
                    }),
                    ...(!isActive && {
                      '& .MuiStepLabel-label': {
                        color: 'text.secondary',
                        fontWeight: 400,
                      },
                    }),
                  },
                }}
              >
                <StepLabel StepIconComponent={CustomStepIcon}>
                  {label}
                </StepLabel>
              </StepButton>
            </Step>
          );
        })}
      </Stepper>
    </Box>
  );
};
